import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ClientUser } from '@digitaltattoo/types';

interface AuthState {
  isAuthenticated: boolean;
  user: ClientUser | null;
  token: string | null;
  refreshToken: string | null;
  loading: boolean;
  error: string | null;
  emailVerificationRequired: boolean;
}

// Check if user is already authenticated on app start
const token = localStorage.getItem('client_token');
const refreshToken = localStorage.getItem('client_refresh_token');
const userStr = localStorage.getItem('client_user');
const user = userStr ? JSON.parse(userStr) : null;

const initialState: AuthState = {
  isAuthenticated: !!token && !!user,
  user: user,
  token: token,
  refreshToken: refreshToken,
  loading: false,
  error: null,
  emailVerificationRequired: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login actions
    loginStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<{ user: ClientUser; token: string; refreshToken: string }>) => {
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.loading = false;
      state.error = null;
      state.emailVerificationRequired = !action.payload.user.emailVerified;
      
      // Persist to localStorage
      localStorage.setItem('client_token', action.payload.token);
      localStorage.setItem('client_refresh_token', action.payload.refreshToken);
      localStorage.setItem('client_user', JSON.stringify(action.payload.user));
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = action.payload;
      state.emailVerificationRequired = false;
      
      // Clear localStorage
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_refresh_token');
      localStorage.removeItem('client_user');
    },

    // Registration actions
    registerStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    registerSuccess: (state, action: PayloadAction<{ user: ClientUser; requiresEmailVerification: boolean }>) => {
      state.loading = false;
      state.error = null;
      state.emailVerificationRequired = action.payload.requiresEmailVerification;
      // Don't set as authenticated until email is verified
    },
    registerFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
      state.emailVerificationRequired = false;
    },

    // Logout action
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = null;
      state.emailVerificationRequired = false;
      
      // Clear localStorage
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_refresh_token');
      localStorage.removeItem('client_user');
    },

    // Token refresh actions
    refreshTokenStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    refreshTokenSuccess: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.loading = false;
      state.error = null;
      
      // Update localStorage
      localStorage.setItem('client_token', action.payload.token);
      localStorage.setItem('client_refresh_token', action.payload.refreshToken);
    },
    refreshTokenFailure: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.loading = false;
      state.error = action.payload;
      state.emailVerificationRequired = false;
      
      // Clear localStorage
      localStorage.removeItem('client_token');
      localStorage.removeItem('client_refresh_token');
      localStorage.removeItem('client_user');
    },

    // Email verification actions
    emailVerificationStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    emailVerificationSuccess: (state) => {
      state.loading = false;
      state.error = null;
      state.emailVerificationRequired = false;
      if (state.user) {
        state.user.emailVerified = true;
        localStorage.setItem('client_user', JSON.stringify(state.user));
      }
    },
    emailVerificationFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Password reset actions
    passwordResetStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    passwordResetSuccess: (state) => {
      state.loading = false;
      state.error = null;
    },
    passwordResetFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update user profile
    updateUser: (state, action: PayloadAction<ClientUser>) => {
      state.user = action.payload;
      localStorage.setItem('client_user', JSON.stringify(action.payload));
    },

    // Clear error
    clearError: (state) => {
      state.error = null;
    },

    // Set email verification required
    setEmailVerificationRequired: (state, action: PayloadAction<boolean>) => {
      state.emailVerificationRequired = action.payload;
    },
  },
});

export const {
  // Login
  loginStart,
  loginSuccess,
  loginFailure,
  
  // Registration
  registerStart,
  registerSuccess,
  registerFailure,
  
  // Logout
  logout,
  
  // Token refresh
  refreshTokenStart,
  refreshTokenSuccess,
  refreshTokenFailure,
  
  // Email verification
  emailVerificationStart,
  emailVerificationSuccess,
  emailVerificationFailure,
  
  // Password reset
  passwordResetStart,
  passwordResetSuccess,
  passwordResetFailure,
  
  // User management
  updateUser,
  clearError,
  setEmailVerificationRequired,
} = authSlice.actions;

export default authSlice.reducer;
